import "es6-promise/auto";

import {Component, lr} from "xm-ui";
import {MessengerContainerComponent} from "./components/MessengerContainerComponent";
import {IMessengerApiResponse} from "./components/IMessengerApiResponse";
import {IMessengerOptions} from "./components/IMessengerOptions";
import {WindowVisibilityService} from './utils/WindowVisibilityService';
import {registerError} from "./utils/registerError";
import {IMessengerHandler} from "./components/IMessengerHandler";
import {DefaultMessengerHandler} from "./components/DefaultMessengerHandler";

class MessengerApi {
    private options: IMessengerOptions;

    private readonly hostElement: JQuery;

    private shownWindowId: string;
    private currentMessengerHandler: IMessengerHandler | null;
    private readonly defaultMessengerHandler: IMessengerHandler;
    private readonly messengerHandlerMap: Record<string, IMessengerHandler>;

    private socketConnection: SignalR.Hub.Connection;
    private hubProxy: SignalR.Hub.Proxy;
    private isConnected: boolean;

    constructor(options?: IMessengerOptions) {
        this.options = $.extend(true, this.getDefaultOptions(), options);
        this.hostElement = $('<div>').appendTo(document.body);
        this.currentMessengerHandler = null;
        this.defaultMessengerHandler = new DefaultMessengerHandler();
        this.messengerHandlerMap = Object.create(null);
    }

    public connect() {
        this.socketConnection = $.hubConnection(this.options.socketHubUrl, { useDefaultPath: false });
        this.hubProxy = this.socketConnection.createHubProxy(this.options.hubName);

        const isVisible = false;

        this.socketConnection.error((e: SignalR.ConnectionError) => {
            if (e.source === 'TimeoutException')
                return;

            registerError(e);
        });

        this.socketConnection.connectionSlow(() => {
            registerError('connectionSlow');
        });

        this.socketConnection.stateChanged((state) => {
            this.isConnected = state.newState === $.signalR.connectionState.connected;
        });

        this.socketConnection.disconnected(() => {
            setTimeout(() => {
                this.wsStart();
            }, this.options.socketReconnectionTimeout);
        });

        this.hubProxy.on('getActivityStatus', () => {
            this.wsInvoke('sendPageActivityStatus', isVisible, (new Date()).getTime());
        });

        this.hubProxy.on('getWindow', this.onGetWindow.bind(this));

        this.hubProxy.on('closeWindow', () => {
            if (this.currentMessengerHandler != null) {
                this.currentMessengerHandler.hideMessenger();
            }
        });

        this.hubProxy.on('reconnect', () => {
            this.socketConnection.stop();
            this.wsStart();
        });

        WindowVisibilityService.change((isVisible: boolean) => {
            if (!this.isConnected)
                return;

            if (isVisible) {
                this.hubProxy.invoke('onPageActivate', (new Date()).getTime());
            } else {
                this.hubProxy.invoke('onPageDeactivate', (new Date()).getTime());
            }
        });

        this.wsStart();
    }

    public listen(event: string, listener: (data: any) => void) {
        this.hostElement.on(event, listener);
    }

    public config(option: string | Partial<IMessengerOptions>, value: any) {
        if (typeof(option) === "string") {
            (this.options as any)[option] = value;
        } else {
            this.options = $.extend(true, this.options, option);
        }
    }

    public set(key: string, value: any) {
        this.options.data = this.options.data || {};
        this.options.data[key] = value;
    }

    public registerHandler(displayType: string, handler: IMessengerHandler) {
        this.messengerHandlerMap[displayType] = handler;
    }

    private wsStart() {
        this.socketConnection.start()
            .done(() => {
                const params = $.extend({
                    url: this.getPageUrl(),
                    sentWindow: this.shownWindowId,
                    notActive: !WindowVisibilityService.getVisibilityStatus()
                }, this.options.initParams);
                this.wsInvoke('onConnectionInit', params);
                this.hostElement.trigger('connect', this);
            });
    }

    private wsInvoke(methodName: string, ...args: any[]) {
        if (!this.isConnected)
            return;

        return new Promise<any>((resolve, reject) => {
            this.hubProxy.invoke(methodName, ...args)
                .then(response => resolve(response))
                .fail((e: any) => {
                    reject(e);
                    registerError(e, methodName);
                });
        });
    }

    private async onGetWindow(response: IMessengerApiResponse) {
        if (this.shownWindowId) {
            registerError('Window already shown');
            return;
        }

        this.shownWindowId = response.LabelId;

        this.currentMessengerHandler = this.messengerHandlerMap[response.DisplayType] || this.defaultMessengerHandler;

        let optionsWithApiResponse = {
            ...this.options,
            response: response
        };

        this.wsInvoke('windowSent', response.Uid);

        await new Promise(resolve => {
            setTimeout(resolve, response.Delay || 0);
        });

        const messengerIsShown = await this.currentMessengerHandler.tryShowMessenger(this.hostElement, optionsWithApiResponse, {
            hide: () => this.wsInvoke('windowClosed', this.shownWindowId)
        });

        if (messengerIsShown) {
            this.wsInvoke('windowShown', this.shownWindowId);
        }
    }

    private getPageUrl() {
        return document.location.href;
    }

    private getPageLoadId() {
        return (window as any)._abc_plid_ || ((window as any)._abc_plid_ = (new Date()).getTime());
    }

    private getDefaultOptions(): Partial<IMessengerOptions> {
        return $.extend({}, {
            apiUrl: '//l.millionlakes.com/api/UserLabel/{action}',
            socketHubUrl: '//l.millionlakes.com/signalr',
            hubName: 'pageHub',
            data: {
                pageUrl: this.getPageUrl()
            },
            initParams: {
                pageLoad: this.getPageLoadId()
            },
            socketReconnectionTimeout: 2000
        });
    }
}

let api: any = new MessengerApi((window as any)._abc_msg_options_);
let queueName = (window as any)._abc_msg_queue_ || 'abc_msg';

(function (publicApi, slice) {
    let queue = publicApi.q || [];

    for (let i = 0; i < queue.length; i++) {
        let args = queue[i];
        if (args && args.length && args[0] in api) {
            api[args[0]].apply(api, slice.call(args, 1));
        }
    }

    if (!publicApi.loaded) {
        publicApi = (window as any)[queueName] = function (action: string) {
            if (action in api) {
                api[action].apply(api, slice.call(arguments, 1));
            }
        };

        publicApi.loaded = true;
    }
})((window as any)[queueName] || {}, Array.prototype.slice);



// WEBPACK FOOTER //
// ./messenger.ts